import React, { useState } from "react";
import { useSelector } from "react-redux";
import { TextField, Autocomplete, createFilterOptions } from "@mui/material";
import { Control, Controller, FieldValues, useWatch } from "react-hook-form";

import { RootState } from "../../../store";
import { IServiceTag } from "../../../interfaces/ServiceTagsInterface";
import ServiceTagCreateEdit from "../../ServiceTags/ServiceTagCreateEdit";

interface Props {
  control: Control<FieldValues>;
  name: string;
  setValue: (name: string, value: string) => void;
}

interface OptionHash {
  [P: string]: IServiceTag;
}

interface SpecialityOption extends Partial<IServiceTag> {
  inputValue?: string;
}

const filter = createFilterOptions<SpecialityOption>();

function SpecialitySearch({ control, name, setValue }: Props): React.ReactNode {
  const { specialities } = useWatch({ control });

  const options: IServiceTag[] = useSelector(
    (state: RootState) =>
      state.serviceTags.collection.filter((item: IServiceTag) => Boolean(item.noun)) || []
  );

  const optionsHash: OptionHash = options.reduce(
    (accumulator, current) => ({ ...accumulator, ...{ [current.id]: current } }),
    {}
  );

  const [openDialog, toggleDialog] = useState(false);
  const [dialogValue, setDialogValue] = useState({
    noun: "",
    tag: ""
  });

  const handleCloseDialog = () => {
    setDialogValue({
      noun: "",
      tag: ""
    });
    toggleDialog(false);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, ...rest }, fieldState: { error } }) => (
        <>
          <Autocomplete
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...rest}
            freeSolo
            selectOnFocus
            clearOnBlur
            handleHomeEndKeys
            options={options as SpecialityOption[]}
            value={optionsHash[specialities ? specialities[0] : null] || null}
            getOptionLabel={(option) => {
              // value selected with enter, right from the input
              if (typeof option === "string") return option;

              // add "xxx" option created dynamically
              if (option.inputValue) return option.inputValue;

              // regular option
              return option?.noun || "";
            }}
            onChange={(_, newValue) => {
              if (typeof newValue === "string") {
                toggleDialog(true);
                setDialogValue({
                  noun: newValue,
                  tag: newValue.charAt(0).toUpperCase() + newValue.slice(1).toLowerCase()
                });
              } else if (newValue && newValue.inputValue) {
                toggleDialog(true);
                setDialogValue({
                  noun: newValue.inputValue,
                  tag:
                    newValue.inputValue.charAt(0).toUpperCase() +
                    newValue.inputValue.slice(1).toLowerCase()
                });
              } else {
                onChange(newValue ? [newValue.id] : []);
              }
              setValue("speciality", "");
            }}
            filterOptions={(ops, params) => {
              const { inputValue } = params;
              const filtered = filter(ops, params);
              const isExisting = options.some((option) => inputValue === option.noun);

              if (inputValue !== "" && !isExisting) {
                filtered.push({
                  inputValue,
                  noun: `Create "${inputValue}"`
                });
              }

              return filtered;
            }}
            renderOption={(props, option) => (
              // eslint-disable-next-line react/jsx-props-no-spreading
              <li {...props} key={option.id || option.noun}>
                {option?.noun || ""}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...params}
                variant="outlined"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                label="Select Speciality"
                margin="dense"
                error={!!error}
                helperText={error?.message || null}
                sx={{ my: 0 }}
              />
            )}
          />

          {openDialog && (
            <ServiceTagCreateEdit
              onClose={handleCloseDialog}
              initialNoun={dialogValue.noun}
              initialTag={dialogValue.tag}
              onSave={(createdTag) => {
                onChange([createdTag.id]);
                handleCloseDialog();
              }}
            />
          )}
        </>
      )}
    />
  );
}

export default SpecialitySearch;
